import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function AuditLog() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ action: "", actor: "", start: "", end: "" });
  const qs = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  };
  const load = () => api.get(`/admin/audit-log?${qs()}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { api.get("/admin/audit-log/actions").then((r) => setActions(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/audit-log/export?${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "solera-audit-log.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-4xl" data-testid="admin-audit-log">
      <div className="flex gap-2 flex-wrap mb-4" data-testid="audit-quick-filters">
        {[
          ["All", ""],
          ["Admin access", "admin."],
          ["Invites", "*invite*"],
          ["Deal submissions", "deal_inquiry."],
        ].map(([label, val]) => (
          <button key={label} type="button"
            onClick={() => setFilters({ action: val, actor: "", start: "", end: "" })}
            className={`t-label px-3 py-1 border ${filters.action === val ? "border-[#C69A4E] !text-[#E8E6E1]" : "border-[#24272B] hover:!text-[#E8E6E1]"}`}
            data-testid={`audit-quick-${label.toLowerCase().replace(/ /g, "-")}`}>{label}</button>
        ))}
      </div>
      <div className="flex items-end gap-3 flex-wrap mb-6">
        <div>
          <span className="t-cap text-muted-s block mb-1">Action</span>
          <select className="input-solera" value={actions.includes(filters.action) ? filters.action : ""} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-filter-action">
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <span className="t-cap text-muted-s block mb-1">Actor</span>
          <input className="input-solera" placeholder="email or partner" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} data-testid="audit-filter-actor" />
        </div>
        <div>
          <span className="t-cap text-muted-s block mb-1">From</span>
          <Popover>
            <PopoverTrigger asChild>
              <button className="input-solera fig text-left" data-testid="audit-filter-start">{filters.start || "Any date"}</button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#14171A] border-[#24272B] text-bone" align="start">
              <Calendar mode="single" selected={filters.start ? new Date(filters.start) : undefined}
                onSelect={(dt) => setFilters({ ...filters, start: dt ? dt.toISOString().slice(0, 10) : "" })} />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <span className="t-cap text-muted-s block mb-1">To</span>
          <Popover>
            <PopoverTrigger asChild>
              <button className="input-solera fig text-left" data-testid="audit-filter-end">{filters.end || "Any date"}</button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#14171A] border-[#24272B] text-bone" align="start">
              <Calendar mode="single" selected={filters.end ? new Date(filters.end) : undefined}
                onSelect={(dt) => setFilters({ ...filters, end: dt ? dt.toISOString().slice(0, 10) : "" })} />
            </PopoverContent>
          </Popover>
        </div>
        <button className="btn-ghost" onClick={exportCsv} data-testid="audit-export-button">Download CSV</button>
      </div>
      <p className="t-small text-muted-s mb-4">An append-only record of sensitive administrative and partner actions.</p>
      <div className="space-y-0">
        {rows.map((r) => (
          <div key={r.audit_id} className="hairline py-4 flex gap-6 items-baseline flex-wrap" data-testid={`audit-row-${r.audit_id}`}>
            <span className="t-cap text-muted-s fig">{r.created_at?.slice(0, 16).replace("T", " ")} UTC</span>
            <span className="t-label">{r.action}</span>
            <span className="t-cap text-muted-s">{r.actor}</span>
            {r.detail && <span className="t-small text-bone measure">{r.detail}</span>}
          </div>
        ))}
        {rows.length === 0 && <p className="t-small text-muted-s">No activity recorded for these filters.</p>}
      </div>
    </div>
  );
}
